// useBluetooth.ts

import { useRef, useState } from "react";
import { useBluetoothManager } from "./bluetoothContext";
import { bleInfoTable } from "./deviceProfiles";
import { BLUETOOTH_DC_REASONS, BLUETOOTH_STATES } from "./bluetoothConstants";

export function useBluetooth({
  handleSendErrorMessage,
}: {
  handleSendErrorMessage: (msg: string) => void;
}) {
  const manager = useBluetoothManager();

  const [connected, setConnected] = useState(false);
  const [isWifiConnected, setIsWifiConnected] = useState(false);
  const [currentNetwork, setCurrentNetwork] = useState("");
  const [bluetoothState, _setBluetoothState] = useState(
    BLUETOOTH_STATES.DISCONNECTED,
  );
  const bluetoothStateRef = useRef<string>(BLUETOOTH_STATES.DISCONNECTED);
  const wifiConnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const wifiDisconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const wifiTargetRef = useRef<string>("");
  const wIsDisconnectingRef = useRef<boolean>(false);
  const wConnectDisconnectRef = useRef<string>("");

  const setBluetoothState = (state: string) => {
    _setBluetoothState(state);
    bluetoothStateRef.current = state;
  };

  async function connect() {
    try {
      setBluetoothState(BLUETOOTH_STATES.CONNECTING);
      if (!manager.checkSupportBLE()) {
        setBluetoothState(BLUETOOTH_STATES.UNSUPPORTED);
        return false;
      }

      manager.setOnDisconnectCallback((reason: number) => {
        setConnected(false);
        setBluetoothState(BLUETOOTH_STATES.DISCONNECTED);
        if (reason !== BLUETOOTH_DC_REASONS.NONE) {
          console.error("Disconnected due to an error");
          let errorMsg;
          switch (reason) {
            case BLUETOOTH_DC_REASONS.DEVICE_RESTART:
              errorMsg = `Bluetooth connection was lost, possibly due to a device restart. Please refresh the page and try again.`;
              break;
            case BLUETOOTH_DC_REASONS.SETUP:
              errorMsg = `Bluetooth connection was lost during setup. Please refresh the page and try again.`;
              break;
            default:
              errorMsg = `Bluetooth connection was lost. Please refresh the page and try again.`;
              break;
          }
          handleSendErrorMessage(errorMsg);
        }
      });

      await manager.requestDevice(bleInfoTable.services);

      await manager.connect();

      await manager.setupServices(
        bleInfoTable.services,
        bleInfoTable.characteristics,
      );

      console.log("Connected to Bluetooth device, clearing wifi state...");
      manager.readBool("scan", "active").then((isActive) => {
        console.log(`Initial wifi active status: ${isActive}`);
        setIsWifiConnected(isActive);
      });

      console.log("Subscribing to Bluetooth notifications...");
      manager.subscribe("scan", "active", async (value: DataView) => {
        const bytes = new Uint8Array(value.buffer);
        console.log(
          `Received wifi active status: ${bytes[0]} for bluetooth state ${bluetoothStateRef.current}`,
        );
        const isActive = bytes[0] === 1 ? true : false;

        try {
          setIsWifiConnected(isActive);
          if (!isActive) {
            setCurrentNetwork("");
          }
          if (bluetoothStateRef.current === BLUETOOTH_STATES.WIFI_CONNECTING) {
            console.log(
              `Wifi got connected successfully, wIsDisconnectingRef.current: ${wIsDisconnectingRef.current}; isActive: ${isActive}; bluetoothStateRef.current: ${bluetoothStateRef.current}; wifiTargetRef.current: ${wifiTargetRef.current}`,
            );
            if (!wIsDisconnectingRef.current && isActive) {
              // possibly successful connection
              // first check wifiName to make sure the wifi was updated to the right ssid
              let wifiName = "";
              for (let i = 0; i < 10; i++) {
                try {
                  wifiName = await manager.read("connect", "name", 10000);
                } catch (error) {
                  await new Promise((r) => setTimeout(r, 1000));
                  continue;
                }
                break;
              }
              console.log(
                `Got wifi name ${wifiName} after connection status update`,
              );
              if (wifiName === wifiTargetRef.current) {
                setCurrentNetwork(wifiName);
                setBluetoothState(BLUETOOTH_STATES.CONNECTED);
                if (wifiConnectTimeoutRef.current) {
                  clearTimeout(wifiConnectTimeoutRef.current);
                  wifiConnectTimeoutRef.current = null;
                }
              }
            } else if (wIsDisconnectingRef.current && !isActive) {
              console.log(
                `Wifi disconnected successfully, wIsDisconnectingRef.current: ${wIsDisconnectingRef.current}; isActive: ${isActive}; bluetoothStateRef.current: ${bluetoothStateRef.current}; wifiTargetRef.current: ${wifiTargetRef.current}`,
              );
              // successful disconnection
              setCurrentNetwork("");
              if (wifiDisconnectTimeoutRef.current) {
                clearTimeout(wifiDisconnectTimeoutRef.current);
                wifiDisconnectTimeoutRef.current = null;
              }
              if (wConnectDisconnectRef.current !== "") {
                // connect directly after disconnect
                startWifiConnect(
                  wifiTargetRef.current,
                  wConnectDisconnectRef.current,
                  false,
                );
                wConnectDisconnectRef.current = "";
              } else {
                // just disconnect
                setBluetoothState(BLUETOOTH_STATES.CONNECTED);
              }
            }
          }
        } catch (error) {
          const errorStr =
            error instanceof Error ? error.message : String(error);
          if (errorStr.includes("GATT Service no longer exists")) {
            manager.disconnect();
          }
        }
      });

      setBluetoothState(BLUETOOTH_STATES.CONNECTED);
      setConnected(true);
    } catch (error) {
      console.error(`Connect Error: ${error}`);
      const errorStr = error instanceof Error ? error.message : String(error);
      setBluetoothState(BLUETOOTH_STATES.DISCONNECTED);
      setConnected(false);
      manager.disconnect(BLUETOOTH_DC_REASONS.NONE);
      if (errorStr.includes("User cancelled")) {
        return true;
      }
      return false;
    }
    return true;
  }

  async function scanWifi(): Promise<string[]> {
    try {
      setBluetoothState(BLUETOOTH_STATES.SCANNING);
      const ssids = await Promise.all([
        manager.read("scan", "list1"),
        manager.read("scan", "list2"),
        manager.read("scan", "list3"),
        manager.read("scan", "list4"),
        manager.read("scan", "list5"),
      ]).then((results) =>
        results
          .join("")
          .split(",")
          .map((ssid) => {
            const [name, connected] = ssid.split("$");
            if (connected === "1") {
              setCurrentNetwork(name);
            }
            return name;
          })
          .filter((ssid) => !!ssid),
      );

      setBluetoothState(BLUETOOTH_STATES.CONNECTED);

      return ssids;
    } catch (error) {
      const errorStr = error instanceof Error ? error.message : String(error);
      if (errorStr.includes("GATT Service no longer exists")) {
        manager.disconnect(BLUETOOTH_DC_REASONS.DEVICE_RESTART);
      }
      console.error(`Scan Wifi Error: ${error}`);
      return [];
    }
  }

  async function startWifiDisconnect(ssid: string = "") {
    setBluetoothState(BLUETOOTH_STATES.WIFI_CONNECTING);
    wIsDisconnectingRef.current = true;

    try {
      await manager.writeStr("connect", "disconnect", ssid);
    } catch (error) {
      const errorStr = error instanceof Error ? error.message : String(error);
      if (errorStr.includes("GATT Service no longer exists")) {
        manager.disconnect(BLUETOOTH_DC_REASONS.DEVICE_RESTART);
      } else {
        // this usually times out, not a problem wifi will still connect
        console.log(`Error writing wifi connect info: ${error}`);
      }
    }

    // timeout after 2 minutes to reset wifi state if connection fails, since we won't receive a notification from the device in that case
    if (wifiDisconnectTimeoutRef.current) {
      clearTimeout(wifiDisconnectTimeoutRef.current);
    }
    wifiDisconnectTimeoutRef.current = setTimeout(async () => {
      if (isWifiConnected) {
        handleSendErrorMessage(
          `There was an error disconnecting from wifi. Please refresh the page and try again.`,
        );
        console.warn("Wifi connect timeout, resetting wifi state...");
      }
      setBluetoothState(BLUETOOTH_STATES.CONNECTED);
    }, 120000);
  }

  async function startWifiConnect(
    ssid: string,
    psk: string,
    shouldDisconnect = true,
  ) {
    setBluetoothState(BLUETOOTH_STATES.WIFI_CONNECTING);
    wIsDisconnectingRef.current = false;

    wifiTargetRef.current = ssid;

    if (shouldDisconnect && isWifiConnected) {
      wConnectDisconnectRef.current = psk;
      return startWifiDisconnect();
    }

    const prefix =
      ssid.length < 10
        ? ssid.length
        : String.fromCharCode("a".charCodeAt(0) + (ssid.length - 10));
    const buff = prefix + ssid + psk;

    try {
      await manager.writeStr("connect", "wpa", buff);
    } catch (error) {
      const errorStr = error instanceof Error ? error.message : String(error);
      if (errorStr.includes("GATT Service no longer exists")) {
        manager.disconnect(BLUETOOTH_DC_REASONS.DEVICE_RESTART);
      }
      // this usually times out, not a problem wifi will still connect
      console.log(`Error writing wifi connect info: ${error}`);
    }

    // timeout after 2 minutes to reset wifi state if connection fails, since we won't receive a notification from the device in that case
    if (wifiConnectTimeoutRef.current) {
      clearTimeout(wifiConnectTimeoutRef.current);
    }
    wifiConnectTimeoutRef.current = setTimeout(async () => {
      let wifiName = "";
      try {
        wifiName = await manager.read("connect", "name", 10000);
      } catch (error) {
        const errorStr = error instanceof Error ? error.message : String(error);
        if (errorStr.includes("GATT Service no longer exists")) {
          manager.disconnect(BLUETOOTH_DC_REASONS.DEVICE_RESTART);
        }
        console.log(
          `Error getting wifi name after wifi connect timeout: ${error}`,
        );
      }

      if (!isWifiConnected || wifiName !== wifiTargetRef.current) {
        handleSendErrorMessage(
          `There was an error connecting to ${wifiTargetRef.current}. Please refresh the page and try again.`,
        );
        console.warn("Wifi connect timeout, resetting wifi state...");
        setBluetoothState(BLUETOOTH_STATES.CONNECTED);
        setCurrentNetwork(wifiName);
        wifiTargetRef.current = "";
      }
    }, 120000);
  }

  function disconnect() {
    manager.disconnect(BLUETOOTH_DC_REASONS.NONE);
    setConnected(false);
    setBluetoothState(BLUETOOTH_STATES.DISCONNECTED);
  }

  return {
    connected,
    connect,
    disconnect,
    bluetoothState,
    scanWifi,
    startWifiConnect,
    isWifiConnected,
    currentNetwork,
  };
}
